import Link from 'next/link'
import { HowStringsSGWorksPage } from '@/index/HowStringsSGWorks'

/**
 * Part-time helper trust page. Shared structure and voice:
 * components/index/HowStringsSGWorks.js.
 *
 * Helper-specific sources - keep claims to these:
 *  - scope of work: /sg/part-time-cleaner-singapore/job-scope-of-part-time-cleaner-service
 *  - pricing: buildDomesticSingaporeFaqs in utils/faq.js and DomesticCostCalculator
 *    (one-off, weekly and biweekly packages; price confirmed before charging)
 *  - "100% Satisfaction Guaranteed / All helpers are background verified"
 *    (StringsSG, 2026-09-27)
 *  - /terms/warranty-claims gives helper bookings no fixed warranty period, so
 *    none is claimed; the satisfaction guarantee is the promise.
 */

// When this page's content last actually changed. Bump it by hand when you edit the copy.
const CONTENT_REVIEWED = '2026-09-27'

const link = (href, label) => (
    <Link href={href} prefetch={false} className="text-orange-600 hover:underline">{label}</Link>
)

export default function HowStringsSGWorksHelper() {
    return (
        <HowStringsSGWorksPage
            path="/sg/part-time-cleaner-singapore/how-stringssg-works"
            title="How StringsSG Part-Time Helper Service Works | Our Service Standards"
            h1="How StringsSG Part-Time Helper Service Works"
            description="StringsSG defines the scope and standard of every helper booking and takes responsibility for the quality. All helpers are background verified, and every booking has our 100% satisfaction guarantee."
            reviewed={CONTENT_REVIEWED}
            intro="StringsSG provides part-time helpers for housekeeping across Singapore, and deep cleaning for move-in and move-out, spring cleaning and post-renovation. We set how every booking is carried out and take responsibility for the result."
            serviceNoun="helper booking"
            people="helpers and cleaners"
            vetting="every helper is background verified, and we check the licences of the ACRA-registered agencies they work for"
            includeSupplies={false}
            standards={
                <p>
                    Every part-time helper booking follows a defined scope of work, so you know what will and will not
                    be done before the helper arrives.{' '}
                    {link('/sg/part-time-cleaner-singapore/job-scope-of-part-time-cleaner-service', 'Part-time helper scope of work')}
                </p>
            }
            pricing={
                <p>
                    The price is calculated when you book, from the service, hours and frequency you choose, and you
                    confirm it before anything is charged - one-off visits and weekly or fortnightly packages are both
                    available.{' '}
                    {link('/sg/part-time-cleaner-singapore/part-time-cleaner-rates-singapore', 'Part-time cleaner rates')}
                </p>
            }
            afterService={
                <p>
                    Every booking is backed by our 100% satisfaction guarantee: if you are not happy, contact us through
                    the app or on WhatsApp and we will make it right. You can rate and review every booking, and
                    reschedule from your account, subject to availability.{' '}
                    {link('/terms/warranty-claims', 'Warranty terms')}
                </p>
            }
            bookHref="/sg/part-time-cleaner-singapore"
            bookLabel="Book a part-time helper"
        />
    )
}
